/**
 * Datos de un documento para la ventana y la página (DESIGN.md §11.7): el documento (`documents`, lectura directa),
 * su favorito y la cadena del criterio (`GET /api/documents/:id/cadena`: vigencia, normas de la cabecera, citado por,
 * cita a, original). Una respuesta que llega después de cambiar de documento se descarta. La cadena se pide solo a
 * documentos del SII (oficios, circulares, resoluciones); en leyes, convenios y sentencias no se dibuja.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase, Document } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { documentsService } from '../services/documents.service';
import { fichaDe } from '../utils/fichaDocumento';
import type { Cadena } from '../utils/cadena';

export const COLUMNAS = 'id, document_id, institution_code, tipo, numero, year, materia, titulo, resumen, contenido_extraido, source_url, fecha_publicacion, fecha_emision, legislacion, created_at';

/** Aviso de que un documento entró o salió de favoritos desde la ventana. */
export const EVENTO_FAVORITO = 'alertai:favorito';
export interface EventoFavorito { id: string; favorito: boolean }

export type CargaCadena = { estado: 'cargando' | 'listo' | 'error'; data: Cadena | null };
export type EstadoDocumento = 'cargando' | 'listo' | 'no-encontrado' | 'error';

/** La cadena se construye sobre los documentos del SII (convenios aparte: no citan oficios). */
export const tieneCadena = (doc: Pick<Document, 'institution_code' | 'tipo'>) =>
  (doc.institution_code || '').toUpperCase() === 'SII' && (doc.tipo || '').toLowerCase() !== 'convenio';

export function useDocumento(id: string | undefined) {
  const { user } = useAuth();
  const [doc, setDoc] = useState<Document | null>(null);
  const [estado, setEstado] = useState<EstadoDocumento>('cargando');
  const [isFavorite, setIsFavorite] = useState(false);
  const [favError, setFavError] = useState(false);
  const [favGuardando, setFavGuardando] = useState(false);
  const [cadena, setCadena] = useState<CargaCadena>({ estado: 'cargando', data: null });

  // Documento vigente: una respuesta que llega después de navegar a otro documento se descarta.
  const vigente = useRef(id);
  vigente.current = id;

  const cargar = useCallback(async () => {
    if (!id) return;
    setEstado('cargando');
    const { data, error } = await supabase.from('documents').select(COLUMNAS).eq('id', id).maybeSingle();
    if (vigente.current !== id) return;
    if (error) {
      // Un id que no es UUID no existe (22P02); lo demás es un error de red o de la base.
      setEstado(error.code === '22P02' ? 'no-encontrado' : 'error');
      return;
    }
    if (!data) {
      setEstado('no-encontrado');
      return;
    }
    setDoc(data as unknown as Document);
    setEstado('listo');
  }, [id]);

  useEffect(() => {
    setDoc(null);
    setIsFavorite(false);
    setFavError(false);
    cargar();
  }, [cargar]);

  // Estado del favorito (lectura directa, como antes); el cambio va por la API.
  const userId = user?.id;
  useEffect(() => {
    if (!userId || !doc) return;
    let vivo = true;
    supabase.from('favorites').select('id').eq('user_id', userId).eq('document_id', doc.id).maybeSingle()
      .then(({ data }) => { if (vivo) setIsFavorite(!!data); });
    return () => { vivo = false; };
  }, [userId, doc]);

  const docId = doc?.id;
  const conCadena = !!doc && tieneCadena(doc);
  const docVigente = useRef(docId);
  docVigente.current = docId;
  const cargarCadena = useCallback(() => {
    if (!docId) return;
    if (!conCadena) { setCadena({ estado: 'listo', data: null }); return; }
    setCadena({ estado: 'cargando', data: null });
    documentsService.getCadena(docId).then(
      data => { if (docVigente.current === docId) setCadena({ estado: 'listo', data }); },
      () => { if (docVigente.current === docId) setCadena({ estado: 'error', data: null }); },
    );
  }, [docId, conCadena]);
  useEffect(() => { cargarCadena(); }, [cargarCadena]);

  const toggleFavorite = async () => {
    if (!doc || favGuardando) return;
    const antes = isFavorite;
    const deDoc = doc.id;
    setIsFavorite(!antes);
    setFavError(false);
    setFavGuardando(true);
    try {
      const r = await documentsService.toggleFavorite(deDoc);
      if (docVigente.current === deDoc) setIsFavorite(r.isFavorite);
      // La lista de fondo (Biblioteca, Favoritos) queda montada bajo la ventana: se entera por este evento.
      window.dispatchEvent(new CustomEvent<EventoFavorito>(EVENTO_FAVORITO, { detail: { id: deDoc, favorito: r.isFavorite } }));
    } catch {
      if (docVigente.current === deDoc) {
        setIsFavorite(antes);
        setFavError(true);
      }
    } finally {
      setFavGuardando(false);
    }
  };

  const ficha = useMemo(() => (doc ? fichaDe(doc) : null), [doc]);

  return { doc, estado, cargar, ficha, isFavorite, favError, favGuardando, toggleFavorite, cadena, cargarCadena, usuarioId: userId };
}

export type DatosDocumento = ReturnType<typeof useDocumento>;
